import { FeatureCollection } from 'geojson';
import {
    ContactCardMap,
    FeaturedProjectMap,
    MapEndpointType,
    MapRequest,
    ProjectListPageMap,
    ProjectListPageSearchResults,
} from '../typings/backend-models';

import createMap from './mapCreator';

declare let mapRequestModel: MapRequest;
declare let projectListPageMap: ProjectListPageMap;
declare let featuredProjectMap: FeaturedProjectMap; // Start page map and for FeaturedProjectMapBlock
declare let contactCardMap: ContactCardMap; // Project page map (but with only one position) and ContactCardMapBlock (with multiple)

const debug = new URL(window.location.href).searchParams.get("debug") === "true";

const getDataFromApi = async <T>(url) => {
    if (debug) console.log(`Fetching from ${url}...`);
    await fetch(url)
        .then((response) => response.json())
        .then((json) => {
            if (debug) console.log(`JSON from ${url}`, json as T, JSON.stringify(json as T));
        })
        .catch((error) => {
            if (debug) console.log(`ERROR from ${url}`, error);
        });
};

const getFeaturedProjectMapMap = () => getDataFromApi<FeaturedProjectMap>(mapRequestModel.mapEndpoint);

const getProjectListPageMap = () => getDataFromApi<ProjectListPageMap>(mapRequestModel.mapEndpoint);

const getProjectListPageSearchResults = () =>
    getDataFromApi<ProjectListPageSearchResults>(`${mapRequestModel.searchEndpointBase}${getSearchParams()}`);

const getSearchParams = () => {
    // Get me from filters/searchText instead
    return ''; // '?q=navnet på prosjektet&catIDs=7,9';
};

const createFeaturesList = (mapItems: any, type: 'project' | 'office' | 'contact-card'): FeatureCollection => {
    const features = mapItems.map((item: any) => {
        const { position, contentLinkId, ...properties } = item;
        const geometry = {
            type: 'Point',
            coordinates: [position.longitude, position.latitude],
        };
        const feature = {
            type: 'Feature',
            id: contentLinkId,
            properties: { ...properties, type },
            geometry,
        };
        return feature;
    });

    const featureCollection: FeatureCollection = {
        type: 'FeatureCollection',
        features,
    };

    return featureCollection;
};

const createProjectListPageMap = async () => {
    const divId = 'project-list-map';
    const containerEl = document.querySelector(`#${divId}`) as HTMLElement;
    if (!containerEl) return;

    const { initialZoom } = containerEl.dataset;
    const { mapItems } = projectListPageMap;
    const features = createFeaturesList(mapItems, 'project');

    createMap({
        mapId: divId,
        layerName: 'projects',
        places: features,
        useSearch: true,
        initialZoom: initialZoom && Number(initialZoom),
    });
};

const createProjectPageContactCardMap = async () => {
    // getContactCardMap();
    const divId = 'project-map';
    const containerEl = document.querySelector(`#${divId}`) as HTMLElement;
    if (!containerEl) return;

    const { initialZoom } = containerEl.dataset;
    const { mapItems } = contactCardMap;
    const { contactCard, googleMapsLink, googleMapsLinkText, position, contentLinkId, friendlyPosition } = mapItems[0];

    const mapItem = { ...contactCard, googleMapsLink, googleMapsLinkText, position, contentLinkId, friendlyPosition };

    const features = createFeaturesList([mapItem], 'contact-card');

    createMap({
        mapId: divId,
        layerName: 'projects',
        places: features,
        center: [position.latitude, position.longitude],
        activePlaceId: contentLinkId,
        initialZoom: initialZoom && Number(initialZoom),
    });
};

const createContactCardMap = async () => {
    const divId = 'contact-card-map';
    const containerEl = document.querySelector(`#${divId}`) as HTMLElement;
    if (!containerEl) return;

    const { initialZoom } = containerEl.dataset;
    const { mapItems } = contactCardMap;

    const items = mapItems.map(({ contactCard, ...rest }) => ({
        ...contactCard,
        ...rest,
    }));

    const features = createFeaturesList(items, 'contact-card');

    const { position, contentLinkId } = mapItems.find((item) => item.contentLinkId === contactCardMap.selectedContentLinkId);

    createMap({
        mapId: divId,
        layerName: 'projects',
        places: features,
        center: [position.latitude, position.longitude],
        activePlaceId: contentLinkId,
        initialZoom: initialZoom && Number(initialZoom),
    });
};

const createFeaturedProjectMapMap = async () => {
    // getFeaturedProjectMapMap();

    const divId = 'featured-map';
    const containerEl = document.querySelector(`#${divId}`) as HTMLElement;
    if (!containerEl) return;

    const { initialZoom } = containerEl.dataset;
    const { mapItems, featuredProjectPageId } = featuredProjectMap;
    const projectDetails = mapItems.find((project) => project.contentLinkId === featuredProjectPageId);
    const { position } = projectDetails;
    const features = createFeaturesList(mapItems, 'project');

    createMap({
        mapId: divId,
        layerName: 'places',
        places: features,
        center: [position.latitude, position.longitude],
        activePlaceId: featuredProjectPageId,
        initialZoom: initialZoom && Number(initialZoom),
    });
};

const veidekkeMap = () => {
    if (typeof mapRequestModel !== 'undefined') {
        switch (mapRequestModel?.endpointType) {
            case MapEndpointType.ProjectPageContactCardMap:
                createProjectPageContactCardMap();
                break;

            case MapEndpointType.ProjectListPageMap:
                createProjectListPageMap();
                break;

            case MapEndpointType.FeaturedProjectMap:
                createFeaturedProjectMapMap();
                break;

            case MapEndpointType.ContactCardMap: // Map with red "contact-card" on top of it (for ContactCardMapBlock)
                createContactCardMap();
                break;
            default:
                break;
        }
    }
};

veidekkeMap();
